/**
 * Optimistic-move overlay for travel-buffer calendar events.
 *
 * When the user drags a meeting that has travel buffers, the backend re-anchors
 * the buffers asynchronously (an apply_event_change Cloud Tasks job), so an
 * immediate refetch still returns them at their OLD position. Without this
 * overlay the buffers would snap back to the old spot and then jump forward
 * again ~1-2s later when the cascade socket fires.
 *
 * This module is a tiny external store (consumed via useSyncExternalStore by
 * useCalendarEvents): `useEventDrag` records where the buffers should be, the
 * events query overlays those positions, and entries clear themselves once a
 * refetch returns the buffer already at the target (the cascade landed) or after
 * a safety expiry (the cascade failed / never ran).
 */

export type PendingMove = { startMs: number; endMs: number; expiresAt: number };

export type PendingMoveInput = { externalEventId: string; startMs: number; endMs: number };

/** A live event observed in a refetch, used to confirm a pending move landed. */
export type ObservedEvent = { externalEventId: string; startMs: number; endMs: number };

/** How long an optimistic move survives unconfirmed before it's dropped. */
export const PENDING_MOVE_TTL_MS = 30_000;

/** Tolerance when deciding a refetch's times match the optimistic target. */
const MATCH_EPSILON_MS = 1_000;

// The current snapshot. Replaced (new reference) on every mutation so
// useSyncExternalStore re-renders; left untouched when nothing changes.
let snapshot: Map<string, PendingMove> = new Map();
const listeners = new Set<() => void>();

function emit(next: Map<string, PendingMove>) {
  snapshot = next;
  for (const listener of listeners) listener();
}

/** Subscribe to snapshot changes; returns an unsubscribe callback (useSyncExternalStore contract). */
export function subscribePendingBufferMoves(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Snapshot accessor for useSyncExternalStore — returns a stable reference when nothing has changed. */
export function getPendingBufferMoves(): Map<string, PendingMove> {
  return snapshot;
}

/** Record (or overwrite) optimistic positions for one or more buffer events. */
export function addPendingBufferMoves(moves: PendingMoveInput[], now: number = Date.now()): void {
  if (moves.length === 0) return;
  const next = new Map(snapshot);
  for (const move of moves) {
    next.set(move.externalEventId, {
      startMs: move.startMs,
      endMs: move.endMs,
      expiresAt: now + PENDING_MOVE_TTL_MS,
    });
  }
  emit(next);
}

/** Drop optimistic moves by id — used when a drag fails and is reverted. */
export function removePendingBufferMoves(externalEventIds: string[]): void {
  if (snapshot.size === 0 || externalEventIds.length === 0) return;
  let changed = false;
  const next = new Map(snapshot);
  for (const id of externalEventIds) {
    if (next.delete(id)) changed = true;
  }
  if (changed) emit(next);
}

/**
 * Drop entries that have been confirmed by fresh data — the observed event is
 * already at the optimistic target — and entries past their expiry. No-op (and
 * no re-render) when nothing changes.
 */
export function confirmPendingBufferMoves(
  observed: ObservedEvent[],
  now: number = Date.now(),
): void {
  if (snapshot.size === 0) return;
  const observedById = new Map(observed.map((e) => [e.externalEventId, e]));
  let changed = false;
  const next = new Map(snapshot);

  for (const [externalEventId, move] of snapshot) {
    if (move.expiresAt <= now) {
      next.delete(externalEventId);
      changed = true;
      continue;
    }
    const live = observedById.get(externalEventId);
    if (
      live &&
      Math.abs(live.startMs - move.startMs) <= MATCH_EPSILON_MS &&
      Math.abs(live.endMs - move.endMs) <= MATCH_EPSILON_MS
    ) {
      next.delete(externalEventId);
      changed = true;
    }
  }

  if (changed) emit(next);
}

/** Test-only: reset the store between cases. */
export function __resetPendingBufferMoves(): void {
  if (snapshot.size > 0) emit(new Map());
}
